/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import authActions from '@sportninja/common/actions/auth'
import { bindActionToPromise } from '@sportninja/common/actions/utils'
import colors from '@sportninja/common/constants/appColors'
import { getErrorMessage } from '@sportninja/common/utils/utils'
import { Alert, App } from 'antd'
import { useContext, useState } from 'react'
import { connect } from 'react-redux'
import { EventCard } from 'src/components/RegistrationEventCard'
import { EventProgress } from 'src/components/RegistrationEventProgress'
import { RegistrationContext } from '../context/RegistrationContext'
import { PageTitle, REGISTRATION_STEPS } from './Main'
import Text from 'src/components/ts-components/Text'
import { ROUTES } from '@sportninja/common/constants/app'
import { Link } from 'react-router-dom'
import Button from 'src/components/ts-components/Button'
import { TextInput } from 'src/components/ts-components/TextInput'
import { PasswordTextInput } from 'src/components/ts-components/PasswordTextInput'

const Login = ({ login }) => {
  const { message } = App.useApp()
  const {
    setStep,
    goBack,
    registration,
    isLogged,
    email,
    setEmail,
    isTeamRep,
    session,
  } = useContext(RegistrationContext)
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  if (isLogged) {
    setStep(REGISTRATION_STEPS.SELECT_YOUR_ROLE)
  }

  const handleNext = async () => {
    try {
      setError(null)
      setLoading(true)
      await login({ email, password })
    } catch (e) {
      if (e?.status === 401) {
        message.error('Invalid Login or Password')
        setError('Invalid Login or Password')
        return
      }
      const erroMessage = getErrorMessage(e)
      setError(erroMessage)
      message.error(erroMessage)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        flex: 1;
        justify-content: center;
        background-color: ${colors.NEUTRAL_800};
        width: 1080px;
        padding: 0 35px 35px 35px;
        border-radius: 16px 16px 0px 0px;
        @media (max-width: 768px) {
          min-width: 0;
          width: 100%;
        }
      `}
    >
      <EventCard eventInfo={registration} />
      <EventProgress eventProgress={1} isTeamRep={isTeamRep} />
      <PageTitle
        title='Login'
        showAlert={false}
        showSuccess={false}
        subtitle={'Please Enter Your Email and Password'}
      />
      <div
        css={css`
          display: flex;
          flex-direction: row;
          gap: 16px;
          @media (max-width: 768px) {
            flex-direction: column;
            gap: 0;
          }
        `}
      >
        <div
          css={css`
            width: 100%;
            display: flex;
            flex-direction: column;
            margin-bottom: 16px;
            gap: 8px;
          `}
        >
          <TextInput
            name='email'
            label='Email *'
            value={email}
            setValue={(v) => {
              setEmail(v)
            }}
            fieldErrors={[]}
            customError={{}}
            placeholder='Email'
            type='text'
            autoComplete='email'
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                if (email && password && !loading) {
                  handleNext()
                }
              }
            }}
          />
        </div>
        <div
          css={css`
            width: 100%;
            margin-bottom: 16px;
            display: flex;
            flex-direction: column;
            gap: 8px;
          `}
        >
          <PasswordTextInput
            name='password'
            label='Password *'
            value={password}
            setValue={(v) => {
              setPassword(v)
            }}
            fieldErrors={[]}
            customError={{}}
            placeholder='Password'
            autoComplete='current-password'
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                if (email && password && !loading) {
                  handleNext()
                }
              }
            }}
          />
        </div>
      </div>
      <Text
        variant='paragraphSmall'
        color={colors.NEUTRAL_50}
        weight='regular'
        style={'align-self: flex-end; margin-bottom: 16px;'}
      >
        <Link
          css={css`
            cursor: pointer;
            &:hover {
              text-decoration: underline;
            }
          `}
          to={`${ROUTES.FORGOT_PASSWORD}?is_registration=true&registration_session_id=${session?.id}&schedule_id=${registration?.id}`}
        >
          Forgot Your Password?
        </Link>
      </Text>
      <div
        css={css`
          border-bottom: 1px solid ${colors.VERY_DARK_GRAYISH_BLUE};
          margin-bottom: 16px;
          width: 100%;
        `}
      />
      {error ? (
        <div
          css={css`
            margin-bottom: 16px;
          `}
        >
          <Alert message={error} type='error' showIcon />
        </div>
      ) : null}
      <div
        css={css`
          display: flex;
          flex-direction: row;
          gap: 8px;
          width: 50%;
          justify-content: flex-end;
          align-self: flex-end;
          @media (max-width: 768px) {
            width: 100%;
            flex-direction: column-reverse;
          }
        `}
      >
        <Button
          onClick={() => {
            goBack(REGISTRATION_STEPS.SIGN_UP)
          }}
          variant='secondary'
          text='BACK'
          disabled={loading}
          isLoading={loading}
        />

        <Button
          onClick={handleNext}
          disabled={email === '' || password === '' || loading}
          variant='primary'
          text='NEXT'
          isLoading={loading}
        />
      </div>
      <div
        css={css`
          display: flex;
          flex: 1;
          width: 100%;
          justify-content: center;
          align-items: center;
          margin-top: 16px;
        `}
      >
        <img
          src={'/images/powered_by_sportninja.png'}
          css={css`
            height: 40px;
            max-width: 250px;
          `}
        />
      </div>
    </div>
  )
}

const mapStateToProps = (state) => {
  return {}
}

const mapDispatchToProps = (dispatch) => {
  return {
    login: bindActionToPromise(dispatch, authActions.login.request),
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(Login)
